import { useEffect, useRef } from 'react';

/**
 * Take the operator to the refusal they just earned.
 *
 * Every sheet in this folder has the same anatomy: the commit button is DOCKED
 * in the footer, and the error renders at the TOP of the scrolling body. That is
 * the right layout — the button is always reachable and the message is where the
 * eye starts — but it means a refusal lands off-screen for anyone who scrolled
 * down to reach the button. Tapping «ثبت پرداخت» and being refused looked exactly
 * like tapping it and nothing happening, so the operator taps again.
 *
 * Three decisions here, each of which is a way the naive version fails:
 *
 *  • **`block: 'start'`, not `'center'`.** Centring assumes the message fits
 *    comfortably in the scroll port. With the on-screen keyboard up — which it
 *    is, because the amount field was just being typed into — the port can be a
 *    couple of hundred pixels tall, and a three-line alert centred in it is
 *    clipped at both ends. Aligning the TOP shows the message from its first
 *    word and lets the rest follow, which is the half worth reading.
 *
 *  • **`behavior: 'auto'`, not `'smooth'`.** A smooth traversal is an animation,
 *    and an animation can be cut short — by a thumb still resting on the glass,
 *    by momentum from the scroll that reached the button, by the keyboard
 *    resizing the viewport underneath it. A half-finished animation is precisely
 *    «it scrolls a bit and does not show the error». An instant jump cannot be
 *    half-finished.
 *
 *  • **`inline` is never asked for.** An inline traversal scrolls EVERY
 *    scrollable ancestor, which is what opened «کلینیک من» shifted sideways with
 *    no way back (`styles.css`, `#root`). There is nothing to move horizontally
 *    in a sheet, so nothing asks.
 *
 * Deferred one frame so the Alert has been laid out: scrolling to an element
 * whose height is still 0 lands in the wrong place, and on a wrapped three-line
 * message that is the difference between reading it and seeing its last line.
 *
 * @param ref      the element wrapping the error
 * @param error    the current message — falsy when there is nothing to show
 * @param attempt  a counter that changes on every COMMIT ATTEMPT. Without it the
 *                 same refusal twice in a row is the same string, nothing
 *                 re-fires, and the second tap looks dead all over again.
 */
export default function useErrorReveal(ref, error, attempt = 0) {
  // Skips the first commit: a sheet that opens with an error already set is
  // showing it at the top anyway, and jumping on mount is motion with no cause.
  const seen = useRef(false);

  useEffect(() => {
    if (!error) { seen.current = true; return undefined; }
    if (!seen.current) { seen.current = true; return undefined; }

    const id = requestAnimationFrame(() => {
      // `?.()` on both: jsdom implements neither `requestAnimationFrame`'s
      // target nor `scrollIntoView`, and an unhandled throw here would take out
      // the refusal this exists to show.
      ref.current?.scrollIntoView?.({ block: 'start', behavior: 'auto' });
    });
    return () => cancelAnimationFrame(id);
  }, [ref, error, attempt]);
}
